import React, { useState } from "react";
import {
  ImportFileActionType,
  ImportFileLabelType,
} from "../enums/importFileActionType";
import { ArrowDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { UtilService } from "../Utils/UtilService";
import { GenericFileInput } from "./GenericInput";
import { Toast } from "./Toast";
import { OverlayLoader } from "./Overlay";
import { ToastResponse } from "../types/ToastProps";
import { CreateFileInput, FileModel } from "../dto/create-file-input";
import { GenericButton } from "./GenericButton";
import { CompanyService } from "../../Modules/Users/services/company.service";
import { UserService } from "../../Modules/Users/services/user.service";
import { ProfileService } from "../../Modules/Users/services/profile.service";
import { DriverService } from "../../Modules/Vehicle/services/DriverService";
import { VehicleService } from "../../Modules/Vehicle/services/VehicleService";
import { AuthService } from "../../Modules/Auth/services/AuthService";
import { ModalProps } from "../types/ModalProps";
import { WorkbookTypes } from "../types/AttachmentTypes";
import { useTranslation } from "react-i18next";

type FileImportFormProps = ModalProps & {
  actionType: ImportFileActionType;
  actionLabel: ImportFileLabelType;
  company?: string;
};

export const FileImportForm: React.FC<FileImportFormProps> = ({
  actionType,
  actionLabel,
  onClose,
  company,
}) => {
  const {t} = useTranslation()  
 const message = t('loading_message');
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isLoading, setIsLoading] = useState(false);

  const downloadTemplate = (): void => {
    let filename: string = "";
    switch (actionType) {
      case ImportFileActionType.COMPANIES:
        filename = "create-companies";
        break;
      case ImportFileActionType.USERS:
        filename = "create-users";
        break;
      case ImportFileActionType.PROFILES:
        filename = "create-profiles";
        break;
      case ImportFileActionType.VEHICLES:
        filename = "create-vehicles";
        break;
      case ImportFileActionType.DRIVERS:
        filename = "create-drivers";
        break;
    }
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL}/sample/${filename}.xlsx`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileError, setSelectedFileError] = useState("");
  const [file, setFile] = useState<FileModel | null>(null);

  const onFileAttachment = async (file: File) => {
    setSelectedFile(file);
    const response = await UtilService.onAttachFile(
      file,
      setSelectedFileError,
      WorkbookTypes
    );
    if (response) {
      setFile(response);
    }
  };
  const companyId = AuthService.getLocalUser()?.user?.companies?.[0]?.id;
  const isFormValid = file !== undefined;

  const resetForm = () => {
    setSelectedFile(null);
    setSelectedFileError("");
  };

  const createAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const createFileInput = new CreateFileInput();
    createFileInput.filename = selectedFile?.name;
    createFileInput.mimetype = selectedFile?.type;
    createFileInput.file = file?.file ?? undefined;
    createFileInput.parentId = company ?? companyId;

    try {
      switch (actionType) {
        case ImportFileActionType.COMPANIES:
          await CompanyService.createFromFile(createFileInput);
          break;
        case ImportFileActionType.USERS:
          await UserService.createFromFile(createFileInput);
          break;
        case ImportFileActionType.PROFILES:
          await ProfileService.createFromFile(createFileInput);
          break;
        case ImportFileActionType.VEHICLES:
          await VehicleService.createFromFile(createFileInput);
          break;
        case ImportFileActionType.DRIVERS:
          await DriverService.createFromFile(createFileInput);
          break;
        default:
          break;
      }
      resetForm();
      onClose(true);
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
        <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg w-full max-w-md p-5 mt-10 mb-10 max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center pb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {actionLabel}
            </h3>
            <button
              onClick={() => onClose(false)}
              className="text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex justify-end">
            <button
              onClick={downloadTemplate}
              className="cursor-pointer bg-blue-100 rounded-lg py-1 px-2 flex items-center w-fit"
            >
              <ArrowDownIcon className="h-4 w-4 mr-2" />
              {t('sample')}
            </button>
          </div>

          <div className="relative">
            <div className="w-full h-100">
              <form className="mt-3 mb-3" onSubmit={createAction}>
                <GenericFileInput
                  containerClassName="my-3"
                  label={t('import_file_type_label')}
                  onChange={onFileAttachment}
                  error={selectedFileError}
                  placeholder={t('file_upload_hint')}
                />

                <div className="mx-5">
                  <GenericButton type="submit" disabled={!isFormValid}>
                    {actionLabel}
                  </GenericButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </>
  );
};
